import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import { Drawer } from 'vaul'
import { useCredit } from '@/hooks'
import type { PbshoppingProductInfo } from '@itsrever/rever-api'
import { calculateAmountDue } from '../helpers/calculate-amount-due'
import { MediaImageIcon } from '@/ui/icons/media-image'
import { ProductDetailsDrawer } from '../product-details/product-details-drawer'
import { XMarkIcon } from '@/ui/icons/xmark'
import { useAppSelector } from '@/redux/hooks'
import { usePostHog } from '@/application/use-posthog'
import { VisuallyHidden } from '@heroui/react'

interface StoreProductCardProps {
    product: PbshoppingProductInfo
    returnedItemId: string
    categoryIndex: number
}

const StoreProductCard = ({
    product,
    returnedItemId,
    categoryIndex
}: StoreProductCardProps) => {
    const {
        t,
        i18n: { language }
    } = useTranslation()

    const { captureEventPosthog } = usePostHog()

    const ecommerceGroup = useAppSelector(
        (store) => store.settingsApi.settings.response.ecommerce_group
    )

    const ecommerceId = useAppSelector(
        (store) => store.settingsApi.settings.response.ecommerce_id
    )

    const [openProduct, setOpenProduct] = useState(false)

    const { remainingCredit } = useCredit()

    const parsedProductPrice = product.price_range?.min?.amount
        ? parseFloat(product.price_range?.min?.amount)
        : null

    const amountDue =
        parsedProductPrice !== null
            ? calculateAmountDue(parsedProductPrice, remainingCredit)
            : null

    const mainImage = product.featured_image_url
    const secondaryImage = product.image_urls?.[1]

    const onOpenProductDetails = (open: boolean) => {
        if (open) {
            captureEventPosthog('FC - View item details', {
                product_id: product.title,
                ecommerceId
            })
        }

        setOpenProduct(open)
    }

    const hasPriceRange = product.price_range?.max?.amount
        ? product.price_range?.min?.amount !== product.price_range?.max?.amount
        : false

    const isHoff = ecommerceGroup === 'hoff'
    const isMisako = ecommerceGroup === 'misako'

    if (isHoff) {
        return (
            <div className="group relative flex h-full cursor-pointer flex-col gap-2.5 bg-white [content-visibility:auto] [&_img]:rounded-none">
                {mainImage ? (
                    <StoreProductImage
                        mainImage={mainImage}
                        secondaryImage={secondaryImage}
                    />
                ) : (
                    <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
                        <MediaImageIcon width={28} height={28} />
                    </div>
                )}
                <div className="flex items-start justify-between gap-2 bg-white">
                    <div className="space-y-0.5">
                        <h3>
                            <Drawer.Root
                                open={openProduct}
                                onOpenChange={onOpenProductDetails}
                                autoFocus
                            >
                                <Drawer.Trigger className="line-clamp-1 text-left font-haas text-xs font-medium before:absolute before:inset-0 before:z-10">
                                    {product.title}
                                </Drawer.Trigger>
                                <Drawer.Portal>
                                    <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
                                    <Drawer.Content
                                        onCloseAutoFocus={(e) =>
                                            e.preventDefault()
                                        }
                                        title={product.title}
                                        className="fixed inset-x-0 bottom-0 z-[100] h-fit overflow-hidden rounded-t-lg bg-white outline-none"
                                    >
                                        <div className="relative">
                                            <Drawer.Close
                                                className="absolute right-3 top-3 rounded-sm p-1 hover:bg-neutral-100"
                                                aria-label="Close"
                                            >
                                                <XMarkIcon />
                                            </Drawer.Close>
                                            <VisuallyHidden aria-hidden>
                                                <Drawer.Title>
                                                    {product.title}
                                                </Drawer.Title>
                                                <Drawer.Description>
                                                    {product.description}
                                                </Drawer.Description>
                                            </VisuallyHidden>
                                            <ProductDetailsDrawer
                                                categoryIndex={categoryIndex}
                                                productId={product.id}
                                                onClose={() =>
                                                    setOpenProduct(false)
                                                }
                                                returnedItemId={returnedItemId}
                                            />
                                        </div>
                                    </Drawer.Content>
                                </Drawer.Portal>
                            </Drawer.Root>
                        </h3>
                        {product.description ? (
                            <p
                                className="line-clamp-2 !text-xs font-normal text-neutral-500"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        product.description
                                    )
                                }}
                            />
                        ) : null}
                    </div>
                    <div>
                        {remainingCredit > 0 ? (
                            <div className="flex flex-col items-end gap-0.5">
                                <p className="font-haas text-sm font-medium leading-none">
                                    {amountDue !== null
                                        ? amountDue?.toLocaleString(language, {
                                              style: 'currency',
                                              currency:
                                                  product.price_range?.min
                                                      ?.currency,
                                              minimumFractionDigits:
                                                  amountDue === 0 ? 0 : 2,
                                              maximumFractionDigits: 2
                                          })
                                        : null}
                                </p>
                                <p className="text-xs font-normal text-neutral-600">
                                    {parsedProductPrice?.toLocaleString(
                                        language,
                                        {
                                            style: 'currency',
                                            currency:
                                                product.price_range?.min
                                                    ?.currency,
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }
                                    )}{' '}
                                    {t('full_catalog.original_price')}
                                </p>
                            </div>
                        ) : parsedProductPrice !== null ? (
                            <p className="text-sm text-neutral-950">
                                {hasPriceRange ? (
                                    <span>{t('full_catalog.from')}</span>
                                ) : null}
                                {parsedProductPrice?.toLocaleString(language, {
                                    style: 'currency',
                                    currency:
                                        product.price_range?.min?.currency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }

    if (isMisako) {
        return (
            <div className="group relative flex h-full cursor-pointer flex-col gap-2.5 bg-white [content-visibility:auto] [&_img]:rounded-none">
                {mainImage ? (
                    <StoreProductImage
                        mainImage={mainImage}
                        secondaryImage={secondaryImage}
                    />
                ) : (
                    <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
                        <MediaImageIcon width={28} height={28} />
                    </div>
                )}
                <div className="flex items-start justify-between gap-2 bg-white">
                    <div className="space-y-0.5">
                        <h3>
                            <Drawer.Root
                                open={openProduct}
                                onOpenChange={onOpenProductDetails}
                                autoFocus
                            >
                                <Drawer.Trigger className="line-clamp-1 text-left font-inter text-xs font-light leading-normal before:absolute before:inset-0 before:z-10">
                                    {product.title}
                                </Drawer.Trigger>
                                <Drawer.Portal>
                                    <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
                                    <Drawer.Content
                                        onCloseAutoFocus={(e) =>
                                            e.preventDefault()
                                        }
                                        title={product.title}
                                        className="fixed inset-x-0 bottom-0 z-[100] h-fit overflow-hidden rounded-t-lg bg-white outline-none"
                                    >
                                        <div className="relative">
                                            <Drawer.Close
                                                className="absolute right-3 top-3 rounded-sm p-1 hover:bg-neutral-100"
                                                aria-label="Close"
                                            >
                                                <XMarkIcon />
                                            </Drawer.Close>
                                            <VisuallyHidden aria-hidden>
                                                <Drawer.Title>
                                                    {product.title}
                                                </Drawer.Title>
                                                <Drawer.Description>
                                                    {product.description}
                                                </Drawer.Description>
                                            </VisuallyHidden>
                                            <ProductDetailsDrawer
                                                categoryIndex={categoryIndex}
                                                productId={product.id}
                                                onClose={() =>
                                                    setOpenProduct(false)
                                                }
                                                returnedItemId={returnedItemId}
                                            />
                                        </div>
                                    </Drawer.Content>
                                </Drawer.Portal>
                            </Drawer.Root>
                        </h3>
                        {product.description ? (
                            <p
                                className="line-clamp-2 !text-xs font-normal text-neutral-500"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        product.description
                                    )
                                }}
                            />
                        ) : null}
                    </div>
                    <div>
                        {remainingCredit > 0 ? (
                            <div className="flex flex-col items-end gap-0.5">
                                <p className="text-sm font-light leading-normal">
                                    {amountDue !== null
                                        ? amountDue?.toLocaleString(language, {
                                              style: 'currency',
                                              currency:
                                                  product.price_range?.min
                                                      ?.currency,
                                              minimumFractionDigits:
                                                  amountDue === 0 ? 0 : 2,
                                              maximumFractionDigits: 2
                                          })
                                        : null}
                                </p>
                                <p
                                    className="font-inter text-xs font-light text-neutral-500
 line-through"
                                >
                                    {parsedProductPrice?.toLocaleString(
                                        language,
                                        {
                                            style: 'currency',
                                            currency:
                                                product.price_range?.min
                                                    ?.currency,
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }
                                    )}{' '}
                                </p>
                            </div>
                        ) : parsedProductPrice !== null ? (
                            <p className="text-sm text-neutral-950">
                                {hasPriceRange ? (
                                    <span>{t('full_catalog.from')}</span>
                                ) : null}
                                {parsedProductPrice?.toLocaleString(language, {
                                    style: 'currency',
                                    currency:
                                        product.price_range?.min?.currency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="group relative flex h-full cursor-pointer flex-col rounded-lg bg-white shadow-sm transition-shadow [content-visibility:auto] hover:shadow-md">
            {mainImage ? (
                <StoreProductImage
                    mainImage={mainImage}
                    secondaryImage={secondaryImage}
                />
            ) : (
                <div className="flex size-full min-h-0 items-center justify-center rounded-t-lg bg-neutral-50 text-neutral-500">
                    <MediaImageIcon width={28} height={28} />
                </div>
            )}
            <div className="space-y-1.5 rounded-b-lg bg-white p-3">
                <div className="space-y-0.5">
                    <h3>
                        <Drawer.Root
                            open={openProduct}
                            onOpenChange={onOpenProductDetails}
                            autoFocus
                        >
                            <Drawer.Trigger className="line-clamp-1 text-left font-roobert text-sm font-medium before:absolute before:inset-0 before:z-10">
                                {product.title}
                            </Drawer.Trigger>
                            <Drawer.Portal>
                                <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
                                <Drawer.Content
                                    onCloseAutoFocus={(e) => e.preventDefault()}
                                    title={product.title}
                                    className="fixed inset-x-0 bottom-0 z-[100] h-fit overflow-hidden rounded-t-lg bg-white outline-none"
                                >
                                    <div className="relative">
                                        <Drawer.Close
                                            className="absolute right-3 top-3 rounded-sm p-1 hover:bg-neutral-100"
                                            aria-label="Close"
                                        >
                                            <XMarkIcon />
                                        </Drawer.Close>
                                        <VisuallyHidden aria-hidden>
                                            <Drawer.Title>
                                                {product.title}
                                            </Drawer.Title>
                                            <Drawer.Description>
                                                {product.description}
                                            </Drawer.Description>
                                        </VisuallyHidden>
                                        <ProductDetailsDrawer
                                            categoryIndex={categoryIndex}
                                            productId={product.id}
                                            onClose={() =>
                                                setOpenProduct(false)
                                            }
                                            returnedItemId={returnedItemId}
                                        />
                                    </div>
                                </Drawer.Content>
                            </Drawer.Portal>
                        </Drawer.Root>
                    </h3>
                    {product.description ? (
                        <p
                            className="line-clamp-2 !text-xs font-normal text-neutral-500"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(product.description)
                            }}
                        />
                    ) : null}
                </div>
                <div>
                    {remainingCredit > 0 ? (
                        <div className="flex items-center gap-2">
                            <div
                                style={{
                                    boxShadow: '0px 0px 5.7px 0px #FFF inset'
                                }}
                                className="w-fit rounded-full bg-radial-gradient from-brand-800 to-brand-800 px-3 py-0.5 shadow shadow-brand-300"
                            >
                                <p className="text-[0.8125rem] text-white drop-shadow-sm">
                                    {amountDue !== null
                                        ? amountDue?.toLocaleString(language, {
                                              style: 'currency',
                                              currency:
                                                  product.price_range?.min
                                                      ?.currency,
                                              minimumFractionDigits:
                                                  amountDue === 0 ? 0 : 2,
                                              maximumFractionDigits: 2
                                          })
                                        : null}
                                </p>
                            </div>
                            <p className="text-xs font-normal text-neutral-600">
                                {parsedProductPrice?.toLocaleString(language, {
                                    style: 'currency',
                                    currency:
                                        product.price_range?.min?.currency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}{' '}
                                {t('full_catalog.original_price')}
                            </p>
                        </div>
                    ) : parsedProductPrice !== null ? (
                        <p className="text-sm text-neutral-950">
                            {hasPriceRange ? (
                                <span>{t('full_catalog.from')}</span>
                            ) : null}{' '}
                            {parsedProductPrice?.toLocaleString(language, {
                                style: 'currency',
                                currency: product.price_range?.min?.currency,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

export default memo(StoreProductCard)

interface StoreProductImageProps {
    mainImage: string
    secondaryImage?: string
}

const StoreProductImage = ({
    mainImage,
    secondaryImage
}: StoreProductImageProps) => {
    return (
        <figure className="grid">
            <img
                src={mainImage}
                height={290}
                width={230}
                fetchPriority="high"
                loading="eager"
                alt=""
                className="col-start-1 row-start-1 aspect-[4/5] min-h-0 w-full rounded-t-lg object-cover"
            />
            {secondaryImage ? (
                <img
                    src={secondaryImage}
                    height={290}
                    width={230}
                    loading="lazy"
                    decoding="async"
                    alt=""
                    className="col-start-1 row-start-1 aspect-[4/5] h-auto min-h-0 w-full rounded-t-lg object-cover opacity-0 transition-opacity delay-0 duration-200 will-change-[opacity] [transform:translateZ(0)] group-hover:opacity-100"
                />
            ) : null}
        </figure>
    )
}
